import React, { useCallback, useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useNavigate } from 'react-router-dom'

import cn from 'classnames'
import classNames from 'classnames'

import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import { makeStyles, Theme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Link from '@mui/material/Link'
import { styled } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'

import { isAvailableBlockchainProviderToast, restore_account } from 'utils'

import { toast } from 'App'
import { PaidModal } from 'components/PaidModal'

import { useStore } from 'store'

import { ConfirmModal } from '../ConfirmModal'
import { CustomTooltip } from '../CustomTooltip'

import styles from './styles.module.scss'

const PREFIX = 'UserAccountSecurity'

const classes = {
  backDrop: `${PREFIX}-backDrop`,
}

const Root = styled('form')(({ theme: Theme }) => ({
  [`& .${classes.backDrop}`]: {
    backdropFilter: 'blur(3px)',
  },
}))

const initialUserStat = {
  BackedBy: 0,
  BackersCount: 0,
  FollowingCount: 0,
  Id: '',
  PostPerDay: 0,
  PostPerMonth: 0,
  PostPerWeek: 0,
}

interface IProps {
  currentUser?: boolean
  darkTheme?: boolean
  lightTheme?: boolean
}

//Resize
const UserAccountSecurity: React.FC<IProps> = observer(
  ({ currentUser, darkTheme, lightTheme }) => {
    const { profile, loading, mediaQuery, main, twoFactor } = useStore()

    const navigate = useNavigate()

    const [payDialogOpened, setPayDialogOpened] = useState(false)
    const [transactionDialogOpened, setTransactionDialogOpened] =
      useState(false)
    const [isTransactionPending, setIsTransactionPending] = useState(false)

    const [phoneNumberDialogOpened, setPhoneNumberDialogOpened] =
      useState(false)
    const [
      confirmationSMSCodeDialogOpened,
      setConfirmationSMSCodeDialogOpened,
    ] = useState(false)
    const [phoneNumber, setPhoneNumber] = useState('')
    const [confirmationSMSCode, setConfirmationSMSCode] = useState('')
    const [phoneNumberChecked, setPhoneNumberChecked] = useState(true)
    const [timer, setTimer] = useState(0)

    const [QRCodeOpened, setQRCodeOpened] = useState(false)
    const [QRCodeSetupKeyOpened, setQRCodeSetupKeyOpened] = useState(false)
    const [confirmationQRCodeDialogOpened, setConfirmationQRCodeDialogOpened] =
      useState(false)
    const [confirmationQRCode, setConfirmationQRCode] = useState('')
    const [QRCode, setQRCode] = useState('')
    const [QRCodeSetupKey, setQRCodeSetupKey] = useState('')

    const [backupDialogOpened, setBackupDialogOpened] = useState(false)
    const [backupCode, setBackupCode] = useState('')
    const [backupCodeCopied, setBackupCodeCopied] = useState(false)
    const [backupCodeDownloaded, setBackupCodeDownloaded] = useState(false)

    const [deleteAccountDialogOpened, setDeleteAccountDialogOpened] =
      useState(false)
    const [
      isOpenConfirmDeleteAccountModal,
      setIsOpenConfirmDeleteAccountModal,
    ] = useState(false)

    const [clickedMethodBeforePay, setClickedMethodBeforePay] = useState<
      'sms' | 'app' | ''
    >('')

    const { IsPayedForTwoFactor, IsSMSEnabled, IsAuthenticatorConnected } =
      profile.selectedUser

    useEffect(() => {
      backupDialogOpened && getBackupCode()
    }, [backupDialogOpened])

    useEffect(() => {
      QRCodeOpened && getAuthenticatorInfo()
    }, [QRCodeOpened])

    useEffect(() => {
      ;(phoneNumberDialogOpened || QRCodeOpened || payDialogOpened) &&
        checkTransactionStatus()
    }, [phoneNumberDialogOpened, QRCodeOpened, payDialogOpened])

    const handleOpenTransactionDialog = () => {
      setTransactionDialogOpened(true)
    }

    const handleOpenPayDialog = (type: 'sms' | 'app') => {
      if (!profile.isBlockchainUserRoleToast()) return
      if (!isAvailableBlockchainProviderToast()) return
      setPayDialogOpened(true)
      setClickedMethodBeforePay(type)
    }

    const handleCloseTransaction = () => {
      setTransactionDialogOpened(false)
    }

    const handleClosePay = () => {
      setPayDialogOpened(false)
    }

    const checkTransactionStatus = async () => {
      try {
        const response = await twoFactor.checkTransactionStatus()
        if (!response.IsCompleted && !response.TransactionHash) {
          setIsTransactionPending(false)
        } else if (response.IsCompleted && response.TransactionHash) {
          setIsTransactionPending(false)
        } else if (!response.IsCompleted && response.TransactionHash) {
          setIsTransactionPending(true)

          handleClosePhoneNumberDialog()
          handleCloseAuthenticatorApp()
          handleClosePay()
          handleOpenTransactionDialog()
        }
      } catch (e) {
        toast({
          type: 'warning',
          message: 'Error while checking transaction status',
        })
      }
    }

    const handlePaySystemAuth = async () => {
      try {
        await twoFactor.payForTwoFactor()
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsPayedForTwoFactor: true,
        })

        loading.setLoading(false)
        handleClosePay()

        if (clickedMethodBeforePay === 'sms') {
          handleOpenPhoneNumberDialog()
        } else {
          handleOpenAuthenticatorApp()
        }

        toast({
          type: 'success',
          message: 'Paid successfully',
        })
      } catch (e) {
        console.log('e', e)
        return toast({
          type: 'error',
          message: 'Payment failed',
        })
      }
    }

    const handlePayWalletAuth = async (paymentHash: string) => {
      try {
        await twoFactor.addPaymentHashForTwoFactor(paymentHash)
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsPayedForTwoFactor: true,
        })

        loading.setLoading(false)
        handleClosePay()

        toast({
          type: 'success',
          message: 'Paid successfully',
        })
      } catch (e) {
        console.log('e', e)
        return toast({
          type: 'error',
          message: 'Payment failed',
        })
      }
    }

    const handleTurnOffSms = async () => {
      try {
        await twoFactor.disableTwoFactorSMS()
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsSMSEnabled: false,
        })

        if (!profile.selectedUser.IsAuthenticatorConnected) {
          profile.removeTrust(profile?.selectedProfileId)
        }

        toast({
          type: 'success',
          message: 'Two-factor sms authentication has been removed',
        })
      } catch (e) {
        toast({
          type: 'warning',
          message: 'Two-factor sms authentication has not been removed',
        })
      }
    }

    const handleTurnOffAuthenticatorApp = async () => {
      try {
        await twoFactor.disableTwoFactorAuthenticator()
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsAuthenticatorConnected: false,
        })

        if (!profile.selectedUser.IsSMSEnabled) {
          profile.removeTrust(profile?.selectedProfileId)
        }

        toast({
          type: 'success',
          message: 'Two-factor authenticator has been removed',
        })
      } catch (e) {
        toast({
          type: 'warning',
          message: 'Two-factor authenticator has not been removed',
        })
      }
    }

    const twoFactorTrustPeriod = () => {
      let different = 0
      const ONE_DAY = 60 * 60 * 24

      if (IsSMSEnabled || IsAuthenticatorConnected) {
        const qrcodeExpires = main.getLocalStorage(
          `qrcode_expiration_date_${profile.selectedUser.Id}`
        )
        const smsExpires = main.getLocalStorage(
          `sms_expiration_date_${profile.selectedUser.Id}`
        )
        const dateNow = new Date()

        if (qrcodeExpires) {
          const dif = Number(qrcodeExpires) - +dateNow
          if (dif > different) {
            different = dif
          }
        }

        if (smsExpires) {
          const dif = Number(smsExpires) - +dateNow
          if (dif > different) {
            different = dif
          }
        }
      }

      const days = Math.floor(different / (ONE_DAY * 1000))
      return different
        ? `Trusted for ${days === 1 ? days + ' day' : days + ' days'}`
        : ''
    }

    //Pay wallet Transaction dialog----------------------------------------------------------------------------------⬇︎⬇︎⬇︎
    const transactionDialog = (
      <Dialog
        open={transactionDialogOpened}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleCloseTransaction}>x</Button>
        </div>
        <DialogTitle>
          <div className={styles.enterPhoneNumberTitle}>
            Your transaction is pending
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className={styles.enterPhoneNumberNote}>
              It may take some time to process your transaction.{' '}
              <b>You can proceed when it's done.</b>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div className={styles.addPhoneNumber}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              onClick={handleCloseTransaction}
            >
              OK
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    )
    //Pay wallet Transaction dialog----------------------------------------------------------------------------------⬆︎⬆︎⬆︎

    // -------------------------------------------------------------------------------------------------------------------

    // SMS-----------------------------------------------------------------------------------------⬇︎⬇︎⬇︎
    const timeOutCallback = useCallback(
      () => setTimer(currTimer => currTimer - 1),
      []
    )

    useEffect(() => {
      timer > 0 && setTimeout(timeOutCallback, 1000)
    }, [timer, timeOutCallback])

    const resetTimer = function () {
      if (!timer) {
        setTimer(120)
      }
    }

    const handleOpenPhoneNumberDialog = () => {
      setPhoneNumberChecked(false)
      setPhoneNumberDialogOpened(true)
    }

    const handleClosePhoneNumberDialog = () => {
      setPhoneNumberChecked(false)
      setPhoneNumberDialogOpened(false)
    }

    const handleOpenConfirmationSMSCodeDialog = () => {
      setConfirmationSMSCodeDialogOpened(true)
    }

    const handleCloseConfirmationSMSCodeDialog = () => {
      setConfirmationSMSCodeDialogOpened(false)
    }

    const handleCheckPhoneNumber = (event: any) => {
      setPhoneNumberChecked(event.target.checked)
    }

    const handleChangeConfirmationSMSCode = (event: any) => {
      setConfirmationSMSCode(event.target.value)
    }

    const handleSendSms = async (isResend?: boolean) => {
      try {
        await twoFactor.addPhoneNumberForTwoFactor(phoneNumber)

        if (isResend) {
          resetTimer()
        } else {
          handleClosePhoneNumberDialog()
          handleOpenConfirmationSMSCodeDialog()
        }

        toast({
          type: 'success',
          message: `Verification code has been ${isResend ? 'resent' : 'sent'}`,
        })
      } catch (e) {
        console.log('e', e)
        return toast({
          type: 'error',
          message: `Error while ${
            isResend ? 'resending' : 'sending'
          } verification code`,
        })
      }
    }

    const confirmCodeFromMessage = async () => {
      try {
        await twoFactor.confirmCodeFromMessage(confirmationSMSCode)
        profile.setSelectedUser({ ...profile.selectedUser, IsSMSEnabled: true })

        // const dateNow = new Date();
        // const period = 30;
        // main.setLocalStorage(`sms_expiration_date_${profile.selectedUser.Id}`, String(dateNow.setDate(dateNow.getDate() + period)))

        handleCloseConfirmationSMSCodeDialog()
        handleOpenBackupCodeDialog()

        toast({
          type: 'success',
          message: 'Verification code has been confirmed',
        })
      } catch (e) {
        console.log('e', e)
        return toast({
          type: 'error',
          message: 'You have entered wrong verification code',
        })
      }
    }

    // PhoneNumber Dialog
    const phoneNumberDialog = (
      <Dialog
        open={phoneNumberDialogOpened}
        maxWidth={'xs'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleClosePhoneNumberDialog}>x</Button>
        </div>
        <DialogTitle>
          <div className={styles.enterPhoneNumberTitle}>
            Enter your phone number
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className={styles.enterPhoneNumberNote}>
              Enter your phone number to receive a text message with a
              confirmation code. You’ll use the code to confirm this is really
              your number
            </div>
          </DialogContentText>
          <div className={styles.phoneNumber}>
            <PhoneInput
              country={'us'}
              onChange={number => setPhoneNumber(number)}
              autoFormat={true}
              inputProps={{
                required: true,
                autoFocus: true,
              }}
              inputStyle={{
                width: '370px',
                height: '40px',
                color: darkTheme ? '#ffffff' : '#151433',
                backgroundColor: darkTheme ? '#151433' : '#ffffff',
              }}
              buttonStyle={{
                color: darkTheme ? '#ffffff' : '#151433',
                backgroundColor: darkTheme ? '#151433' : '#ffffff',
              }}
              dropdownStyle={{
                position: 'fixed',
                color: darkTheme ? '#6b6b6b' : '#151433',
                backgroundColor: darkTheme ? '#151433' : '#ffffff',
              }}
            />
          </div>
          <div className={styles.checkPhoneNumber}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={handleCheckPhoneNumber}
                  sx={{
                    color: 'var(--textColor)',
                  }}
                />
              }
              label="I read and accepted the"
              sx={{
                fontSize: mediaQuery.minWidth_2200 ? '0.85rem' : '0.7rem',
              }}
            />
            <div className={styles.privacyPolicy}>
              <Link
                href="https://authencity.io/files/Authencity_User_Policy.pdf"
                target="_blank"
                rel="noopener"
              >
                privacy policy
              </Link>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <div className={styles.addPhoneNumber}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              disabled={
                !phoneNumberChecked || !phoneNumber || phoneNumber.length < 5
              }
              onClick={() => handleSendSms()}
            >
              Add Phone Number
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    )

    const confirmationSMSCodeDialog = (
      <Dialog
        open={confirmationSMSCodeDialogOpened}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleCloseConfirmationSMSCodeDialog}>x</Button>
        </div>
        <DialogTitle>
          <div className={styles.enterConfirmationCodeTitle}>
            Enter the confirmation code
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className={styles.enterConfirmationCodeNote}>
              We sent you a text message with your 6-digit code to this number:
              <p className={styles.phoneNumberRepeat}>+{phoneNumber}</p>
            </div>
          </DialogContentText>
          <div className={styles.enterConfirmationCode}>
            <TextField
              autoFocus
              id="code"
              placeholder="Confirmation code"
              fullWidth
              onChange={handleChangeConfirmationSMSCode}
              sx={{
                backgroundColor: darkTheme ? '#151433' : '#ffffff',
                border: '1px solid #ffffff',
                borderRadius: '5px',
                color: darkTheme ? '#ffffff' : '#151433',
              }}
              InputProps={{
                style: { color: darkTheme ? '#ffffff' : '#151433' },
              }}
              InputLabelProps={{
                style: { color: darkTheme ? '#ffffff' : '#151433' },
              }}
            />
          </div>
          <div className={styles.resendCode}>
            {timer > 0 ? (
              <Button
                variant="text"
                size="small"
                color="primary"
                sx={{
                  color: darkTheme ? '#ffffff' : '#151433',
                }}
              >
                Resend code in {timer}
              </Button>
            ) : (
              <Button
                variant="text"
                size="small"
                color="primary"
                disabled={timer > 0}
                onClick={() => handleSendSms(true)}
                sx={{
                  color: darkTheme ? '#ffffff' : '#151433',
                }}
              >
                Resend code
              </Button>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <div className={styles.enterConfirmationCode}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              disabled={!confirmationSMSCode || confirmationSMSCode.length < 6}
              onClick={confirmCodeFromMessage}
            >
              Confirm
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    )
    // SMS-----------------------------------------------------------------------------------------⬆︎⬆︎⬆︎

    // -------------------------------------------------------------------------------------------------------------------

    // AuthenticatorAPP-----------------------------------------------------------------------------------------⬇︎⬇︎⬇︎
    const getAuthenticatorInfo = async () => {
      try {
        const response = await twoFactor.getAuthenticatorInfo()

        setQRCode(response.Item1)
        setQRCodeSetupKey(response.Item2)
      } catch (e) {
        toast({
          type: 'warning',
          message: 'Error while getting authenticator code',
        })
      }
    }

    const handleOpenAuthenticatorApp = () => {
      setQRCodeOpened(true)
    }

    const handleCloseAuthenticatorApp = () => {
      setQRCodeOpened(false)
      setQRCodeSetupKeyOpened(false)
    }

    const handleScanQRCodeDone = () => {
      setQRCodeOpened(false)
      setQRCodeSetupKeyOpened(false)
      setConfirmationQRCodeDialogOpened(true)
    }

    const handleShowQRCodeSetupKey = async (event: any) => {
      event.preventDefault()
      setQRCodeSetupKeyOpened(true)
    }

    const handleOpenConfirmationQRCodeDialog = () => {
      setConfirmationQRCodeDialogOpened(true)
    }

    const handleCloseConfirmationQRCodeDialog = () => {
      setConfirmationQRCodeDialogOpened(false)
    }

    const handleChangeConfirmationQRCode = (event: any) => {
      setConfirmationQRCode(event.target.value)
    }

    const handleConfirmQRCode = async () => {
      try {
        await twoFactor.verifyAuthenticatorCode(confirmationQRCode)
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsAuthenticatorConnected: true,
        })

        // const dateNow = new Date();
        // const period = 30;
        // main.setLocalStorage(`qrcode_expiration_date_${profile.selectedUser.Id}`, String(dateNow.setDate(dateNow.getDate() + period)))

        handleCloseConfirmationQRCodeDialog()
        handleOpenBackupCodeDialog()

        toast({
          type: 'success',
          message: 'Authenticator code has been confirmed',
        })
      } catch (e) {
        console.log('e', e)
        return toast({
          type: 'error',
          message: 'You have entered wrong verification code',
        })
      }
    }

    // QR Dialog
    const QRCodeDialog = (
      <Dialog
        open={QRCodeOpened}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleCloseAuthenticatorApp}>x</Button>
        </div>
        <DialogTitle>
          <div className={styles.linkAuthenticatorScanTitle}>
            Link the app to your Authencity account
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className={styles.linkAuthenticatorScanNote}>
              Use your authentication app to scan this QR code. If you don't
              have an authentication app on your device, you'll need to install
              one now.
            </div>
          </DialogContentText>
          <div className={styles.QRCode}>
            <img src={QRCode}></img>
          </div>
          {QRCodeSetupKeyOpened && (
            <div>
              <div className={styles.linkAuthenticatorCodeTitle}>
                {QRCodeSetupKey}
              </div>
              <div className={styles.linkAuthenticatorCodeNote}>
                Enter a setup key to the authenticator app
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <div className={styles.doneScan}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              onClick={handleScanQRCodeDone}
            >
              Done
            </Button>
          </div>
        </DialogActions>
        {!QRCodeSetupKeyOpened && (
          <DialogActions>
            <div className={styles.cantScanCode}>
              <Link
                href="#"
                onClick={handleShowQRCodeSetupKey}
                sx={{ color: darkTheme ? '#ffffff' : '#2E2B63' }}
              >
                Can't scan the QR code?
              </Link>
            </div>
          </DialogActions>
        )}
      </Dialog>
    )

    // ConfirmationQRCode dialog
    const confirmationQRCodeDialog = (
      <Dialog
        open={confirmationQRCodeDialogOpened}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleCloseConfirmationQRCodeDialog}>x</Button>
        </div>
        <DialogTitle>
          <div className={styles.enterConfirmationCodeTitle}>
            Enter the confirmation code
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div className={styles.enterConfirmationCodeNote}>
              Follow the instructions on the authentication app to link your
              Authencity account. Once the authentication app generates a
              confirmation code, enter it here.
            </div>
          </DialogContentText>
          <div className={styles.enterConfirmationCode}>
            <TextField
              autoFocus
              id="code"
              placeholder="Confirmation code"
              fullWidth
              onChange={handleChangeConfirmationQRCode}
              sx={{
                backgroundColor: darkTheme ? '#151433' : '#ffffff',
                border: '1px solid #ffffff',
                borderRadius: '5px',
                color: darkTheme ? '#ffffff' : '#151433',
              }}
              InputProps={{
                style: { color: darkTheme ? '#ffffff' : '#151433' },
              }}
              InputLabelProps={{
                style: { color: darkTheme ? '#ffffff' : '#151433' },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <div className={styles.enterConfirmationCode}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              disabled={!confirmationQRCode || confirmationQRCode.length < 6}
              onClick={handleConfirmQRCode}
            >
              Confirm
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    )
    // AuthenticatorAPP-----------------------------------------------------------------------------------------⬆︎⬆︎⬆︎

    // -------------------------------------------------------------------------------------------------------------------

    // BACKUP-----------------------------------------------------------------------------------------⬇︎⬇︎⬇︎⬆︎⬆︎⬆︎
    const handleOpenBackupCodeDialog = () => {
      setBackupCodeCopied(false)
      setBackupCodeDownloaded(false)
      setBackupDialogOpened(true)
    }

    const handleCloseBackup = () => {
      setBackupCodeCopied(false)
      setBackupCodeDownloaded(false)
      setBackupDialogOpened(false)
    }

    const handleCallNotification = () => {
      toast({
        type: 'error',
        message:
          'Please, copy or download your backup code before you close the window.',
      })
    }

    const handleCopyBackupCode = () => {
      navigator.clipboard.writeText(backupCode)

      setBackupCodeCopied(true)
      toast({
        type: 'success',
        message: 'Code has been copied',
      })
    }

    const handleDownloadBackupCode = () => {
      const fileData = JSON.stringify(backupCode)

      const blob = new Blob([fileData], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.download = 'Authencity backup code.text'
      link.href = url
      link.click()
      link.remove()

      setBackupCodeDownloaded(true)
      toast({
        type: 'success',
        message: 'Code has been downloaded',
      })
    }

    const handleGenerateBackupCode = async (event: any) => {
      event.preventDefault()

      getBackupCode()

      setBackupCodeCopied(false)
      setBackupCodeDownloaded(false)
      toast({
        type: 'success',
        message: 'New code has been generated',
      })
    }

    const getBackupCode = async () => {
      try {
        const response = await twoFactor.getBackupCode()

        setBackupCode(response)
      } catch (e) {
        toast({
          type: 'warning',
          message: 'Error while getting backup code',
        })
      }
    }

    const backupDialog = (
      <Dialog
        open={backupDialogOpened}
        // onClose={(backupCodeCopied || backupCodeDownloaded) ? handleCloseBackup : handleCallNotification}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button
            onClick={
              backupCodeCopied || backupCodeDownloaded
                ? handleCloseBackup
                : handleCallNotification
            }
          >
            x
          </Button>
        </div>
        <DialogContent>
          <DialogContentText>
            <div className={styles.verifyYourIdentityNote}>
              If you ever lose access to your device, you can use this code to
              verify your identity.
            </div>
          </DialogContentText>
          <DialogContentText>
            <div className={styles.verifyYourIdentityNote}>
              Write it down, or take a screenshot, and keep it some place safe.
              This code can only be used once.
            </div>
          </DialogContentText>
          <div className={styles.verifyYourIdentity}>
            {backupCode}
            <span
              onClick={handleDownloadBackupCode}
              className={darkTheme ? styles.imageAlt : styles.image}
            >
              <SystemUpdateAltIcon />
            </span>
          </div>
        </DialogContent>
        <DialogActions>
          <div className={styles.copyCode}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              disabled={backupCodeCopied}
              onClick={handleCopyBackupCode}
            >
              {backupCodeCopied ? 'Copied!' : 'Copy code'}
            </Button>
          </div>
        </DialogActions>
        <DialogActions>
          <div className={styles.generateCode}>
            <Link
              href="#"
              onClick={handleGenerateBackupCode}
              sx={{ color: darkTheme ? '#ffffff' : '#2E2B63' }}
            >
              Generate a new code
            </Link>
          </div>
        </DialogActions>
      </Dialog>
    )
    // BACKUP-----------------------------------------------------------------------------------------⬆︎⬆︎⬆︎

    // DELETE ACCOUNT-----------------------------------------------------------------------------------------⬇︎⬇︎⬇︎⬆︎⬆︎⬆︎
    const handleOpenDeleteAccountDialog = () => {
      setDeleteAccountDialogOpened(true)
    }

    const handleCloseDeleteAccount = () => {
      setDeleteAccountDialogOpened(false)
    }

    const handleClickDeleteAccount = () => {
      setDeleteAccountDialogOpened(false)
      setIsOpenConfirmDeleteAccountModal(true)
    }

    const deleteUserAccount = async () => {
      try {
        loading.setLoading(true)

        await profile.deleteAccount()

        profile.setSessionProfileData(await profile.getProfileByToken(), true)

        navigate(restore_account())

        toast({
          type: 'success',
          message: 'Account has been deleted',
        })

        loading.setLoading(false)
      } catch (e: any) {
        loading.setLoading(false)

        toast({
          type: 'error',
          message: e?.message,
        })
      }
    }

    const deleteAccountDialog = (
      <Dialog
        open={deleteAccountDialogOpened}
        maxWidth={'sm'}
        PaperProps={{
          style: {
            borderRadius: '10px',
            backgroundColor: darkTheme ? '#151433' : 'white',
          },
        }}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <div className={styles.close}>
          <Button onClick={handleCloseDeleteAccount}>x</Button>
        </div>
        <DialogContent>
          <DialogContentText>
            <div className={styles.deleteAccountTitle}>
              Before you delete your account, please note the following:
            </div>
          </DialogContentText>

          <ul className={styles.deleteAccountList}>
            <li className={styles.deleteAccountDescription}>
              Your account will first be deactivated, along with all the
              messages you have posted, which will no longer be visible. Once
              deactivated, your account and messages can no longer be backed by
              other users. Any AUTH in your profile and message pools will be
              distributed through the normal distribution process.
            </li>

            <li className={styles.deleteAccountDescription}>
              You can restore your account within 6 months. After this period,
              your account and all associated data and messages will be
              permanently removed. Any remaining AUTH in your system balance
              will be returned to Authencity. If you restore your account within
              6 months, all data and messages will be restored.
            </li>

            <li className={styles.deleteAccountDescription}>
              Deleting your account does not burn the NFTs created from your
              posts.
            </li>
          </ul>

          <DialogContentText>
            <div className={styles.deleteAccountTitle}>
              Are you sure you want to proceed with deleting your account?
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div className={styles.deleteAccountButton}>
            <Button
              variant="contained"
              size="large"
              color="error"
              fullWidth
              onClick={handleClickDeleteAccount}
            >
              Delete my account
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    )
    // DELETE ACCOUNT-----------------------------------------------------------------------------------------⬆︎⬆

    return (
      <Root
        className={cn({
          [styles.cartBody]: true,
          [styles.cartBodyCurrent]: currentUser,
        })}
      >
        {transactionDialog}
        {payDialogOpened ? (
          <PaidModal
            isOpen={payDialogOpened}
            setIsOpen={setPayDialogOpened}
            paySystem={handlePaySystemAuth}
            payWallet={handlePayWalletAuth}
          />
        ) : null}
        {phoneNumberDialog}
        {confirmationSMSCodeDialog}
        {QRCodeDialog}
        {confirmationQRCodeDialog}
        {backupDialog}
        {deleteAccountDialog}
        <ConfirmModal
          isOpen={isOpenConfirmDeleteAccountModal}
          setIsOpen={setIsOpenConfirmDeleteAccountModal}
          onClickYes={deleteUserAccount}
          text="Are you sure you want to delete your account?"
        />
        {currentUser && (
          <div style={{ width: '100%' }}>
            <div className={styles.titleBlock}>
              <div className={styles.title}>Security and account access</div>
            </div>
            <div className={styles.subtitleLock}>
              <div className={styles.subtitle}>Two-factor authentication</div>
              <div className={darkTheme ? styles.iconAlt : styles.icon}>
                {IsSMSEnabled || IsAuthenticatorConnected ? (
                  <CustomTooltip
                    title={twoFactorTrustPeriod()}
                    placement="top"
                    // disablePortal
                    children={<LockOutlinedIcon />}
                  />
                ) : (
                  <LockOpenOutlinedIcon />
                )}
              </div>
            </div>
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <FormGroup>
                <FormControlLabel
                  label="SMS Message"
                  control={
                    <Switch
                      size={'small'}
                      checked={IsSMSEnabled}
                      onChange={
                        IsSMSEnabled
                          ? handleTurnOffSms
                          : IsPayedForTwoFactor
                          ? handleOpenPhoneNumberDialog
                          : () => handleOpenPayDialog('sms')
                      }
                      sx={{ float: 'right' }}
                    />
                  }
                  sx={{
                    display: 'block',
                    marginLeft: '0',
                    marginRight: '0',
                    color: darkTheme ? '#ffffff' : '#2E2B63',
                    '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                  }}
                />
              </FormGroup>
              <p className={styles.note}>
                Use your mobile phone to receive a text message with an
                authentication code to enter when you log in to Authencity
              </p>
            </Box>
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <FormGroup>
                <FormControlLabel
                  label="Authentication app"
                  control={
                    <Switch
                      size={'small'}
                      checked={IsAuthenticatorConnected}
                      onChange={
                        IsAuthenticatorConnected
                          ? handleTurnOffAuthenticatorApp
                          : IsPayedForTwoFactor
                          ? handleOpenAuthenticatorApp
                          : () => handleOpenPayDialog('app')
                      }
                      sx={{ float: 'right' }}
                    />
                  }
                  sx={{
                    display: 'block',
                    marginLeft: '0',
                    marginRight: '0',
                    color: darkTheme ? '#ffffff' : '#2E2B63',
                    '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                  }}
                />
              </FormGroup>
              <p className={styles.note}>
                Use an app to get an authentication code to enter when you log
                in to Authencity
              </p>
            </Box>
            <Box
              sx={{ flexGrow: 1, width: '100%', cursor: 'pointer' }}
              onClick={handleOpenBackupCodeDialog}
            >
              <p className={styles.mock}>Backup codes</p>
              <p className={styles.note}>
                Get single-use backup codes so you can log in to Authencity if
                you don’t have access to your two-factor authenticiation options
              </p>
            </Box>

            <div
              className={classNames(
                styles.subtitleLock,
                styles.subtitleLock_dangerZone
              )}
            >
              <div className={styles.subtitle}>Danger zone</div>
            </div>
            <Box
              sx={{ flexGrow: 1, width: '100%', cursor: 'pointer' }}
              onClick={handleOpenDeleteAccountDialog}
            >
              <p className={styles.mock}>Delete my account</p>
              {/*<p className={styles.note}>Delete my account</p>*/}
            </Box>
          </div>
        )}
      </Root>
    )
  }
)

export default UserAccountSecurity
